'use client';

import { ReactNode, useEffect } from 'react';
import { useSession } from 'next-auth/react';
import { initializePostHog, identifyUser, resetUser } from '@/lib/utils/posthog';
import { useAnalytics } from '@/hooks/useAnalytics';

interface AnalyticsProviderProps {
  children: ReactNode;
}

/**
 * Analytics Provider Component
 * Initializes PostHog and manages user identification
 * PostHog automatically handles $pageview events, while custom tracking provides additional context
 * Following NextJS 15 best practices for client-side analytics
 */
export function AnalyticsProvider({ children }: AnalyticsProviderProps) {
  const { data: session, status } = useSession();
  
  // Note: PostHog automatically captures $pageview events
  // Custom tracking via useAnalytics provides additional structured data
  useAnalytics();

  // Initialize PostHog on mount
  useEffect(() => {
    initializePostHog();
  }, []);

  // Handle user identification based on auth session
  useEffect(() => {
    if (status === 'loading') return; // Don't do anything while loading

    if (session?.user) {
      // User is authenticated - identify them
      const userId = session.user.email || session.user.id || 'authenticated-user';
      const userProperties = {
        email: session.user.email,
        name: session.user.name,
        spotify_connected: true,
        // Add any other user properties you want to track
      };
      
      identifyUser(userId, userProperties);
    } else {
      // User is not authenticated - reset identification
      resetUser();
    }
  }, [session, status]);

  return <>{children}</>;
} 